import { lazy, Suspense, useEffect, useState, useCallback, useRef } from 'react';
import { Switch, Route, Router as WouterRouter, useLocation, Redirect } from 'wouter';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/hooks/use-toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { TooltipProvider } from '@/components/ui/tooltip';

import '@/lib/fetch-interceptor';
import { AuthProvider, useAuth } from '@/lib/auth';
import { SubscriptionProvider } from '@/hooks/useSubscription';
import { pixelPageView } from '@/lib/pixel';
import { Sentry } from '@/lib/sentry';
import { onGlobalMatch, onGlobalSubscriptionRefresh, type MatchNotification } from '@/hooks/useWebSocket';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useSubscription } from '@/hooks/useSubscription';
import { NotificationBanner } from '@/components/NotificationBanner';
import { playMatchSound } from '@/lib/matchSound';

import { BannerAd } from '@/components/BannerAd';
import { DesktopSidebar } from '@/components/DesktopSidebar';
import { InstallPrompt } from '@/components/InstallPrompt';
import { InstallNudge } from '@/components/InstallNudge';
import { MatchInstallNudge } from '@/components/MatchInstallNudge';
import { IOSInstallBanner } from '@/components/IOSInstallBanner';
import { AndroidInstallPrompt } from '@/components/AndroidInstallPrompt';
import { SoftPushPrompt } from '@/components/SoftPushPrompt';
import { BehavioralPushModal } from '@/components/BehavioralPushModal';
import { IOSInstallGateModal } from '@/components/IOSInstallGateModal';
import { PushDeclinedBanner } from '@/components/PushDeclinedBanner';
import { InAppBrowserOverlay } from '@/components/InAppBrowserOverlay';
import { usePushPromptController } from '@/hooks/usePushPromptController';
import {
  useBehavioralPushOptIn,
  onBehavioralPushTrigger,
  VARIANT_COPY,
  buildVariantSubtitle,
  buildVariantTitle,
} from '@/hooks/useBehavioralPushOptIn';
import { useActiveTimeAccumulator } from '@/hooks/useActiveTimeAccumulator';
import {
  shouldShowInstallNudge,
  isTriggerDismissed,
  isStandalone,
  trackSession,
} from '@/lib/deviceDetect';
import { isAndroid, isIOS, isInstalled } from '@/lib/installManager';
import { apiUrl } from '@/lib/api-url';
import { useHeartbeat } from '@/hooks/useHeartbeat';

function isChunkError(msg: string | undefined): boolean {
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes('failed to fetch dynamically imported module') ||
    lower.includes('loading chunk') ||
    lower.includes('importing a module script failed') ||
    lower.includes('error loading dynamically imported module') ||
    lower.includes('load failed')
  );
}

function clearSwCachesAndReload() {
  const reload = () => location.reload();
  if (typeof caches !== 'undefined') {
    caches.keys().then((names) =>
      Promise.all(names.map((name) => caches.delete(name)))
    ).then(reload).catch(reload);
  } else {
    reload();
  }
}

function lazyLoad<T extends { default: React.ComponentType<any> }>(
  importFn: () => Promise<T>
) {
  return lazy(() =>
    importFn().catch((err) => {
      if (isChunkError(err?.message) || isChunkError(String(err))) {
        clearSwCachesAndReload();
      }
      throw err;
    })
  );
}

const Login = lazyLoad(() => import('@/pages/Login'));
const Register = lazyLoad(() => import('@/pages/Register'));
const ForgotPassword = lazyLoad(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazyLoad(() => import('@/pages/ResetPassword'));
const Onboarding = lazyLoad(() => import('@/pages/Onboarding'));
const Discover = lazyLoad(() => import('@/pages/Discover'));
const Matches = lazyLoad(() => import('@/pages/Matches'));
const Chat = lazyLoad(() => import('@/pages/Chat'));
const Profile = lazyLoad(() => import('@/pages/Profile'));
const Admin = lazyLoad(() => import('@/pages/Admin'));
const OperatorLogin = lazyLoad(() => import('@/pages/OperatorLogin'));
const OperatorSetup = lazyLoad(() => import('@/pages/OperatorSetup'));
const OperatorDashboard = lazyLoad(() => import('@/pages/OperatorDashboard'));
const Unsubscribe = lazyLoad(() => import('@/pages/Unsubscribe'));
const Privacy = lazyLoad(() => import('@/pages/Privacy'));
const Terms = lazyLoad(() => import('@/pages/Terms'));
const DeleteAccount = lazyLoad(() => import('@/pages/DeleteAccount'));
const Explore = lazyLoad(() => import('@/pages/Explore'));
const VerifyEmail = lazyLoad(() => import('@/pages/VerifyEmail'));
const Landing = lazyLoad(() => import('@/pages/Landing'));
const ExploreCategory = lazyLoad(() => import('@/pages/ExploreCategoryV2'));
const Likes = lazyLoad(() => import('@/pages/Likes'));
const PublicProfile = lazyLoad(() => import('@/pages/PublicProfile'));
const SubscriptionSuccess = lazyLoad(() => import('@/pages/SubscriptionSuccess'));
// 2026-05-12 — new push acceptance page (Group 1 of post-signup routing).
// Replaces the old PostOnboardingInstallPrompt at /install-prompt; that path
// now 302-redirects to /push-prompt for backwards compat with any in-flight
// emails / bookmarks. The old component file is left on disk in case any
// external links still hit a path that resolves to it.
const PushPromptPage = lazyLoad(() => import('@/pages/PushPromptPage'));
// 2026-05-12 — SPA install instructions page, destination of the install
// email's CTA link. Mounted at /install-app (NOT /install — that path is
// owned by the Express ad-lander, see app.ts:414).
const InstallPage = lazyLoad(() => import('@/pages/Install'));
const VipSalesScreen = lazyLoad(() => import('@/pages/VipSalesScreen'));
const OpenInChromeScreen = lazyLoad(() => import('@/pages/OpenInChromeScreen'));
const NotFound = lazyLoad(() => import('@/pages/not-found'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '2px solid rgba(225,29,72,0.2)',
          borderTopColor: '#e11d48',
          animation: 'spin 0.6s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ProtectedRoute({
  component: Component,
  requireOnboarding = true,
}: {
  component: any;
  requireOnboarding?: boolean;
}) {
  const { user, isLoading, token } = useAuth();
  const [location] = useLocation();

  if (!token) {
    if (location === '/') return <Landing />;
    return <Redirect to="/login" />;
  }

  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '2px solid rgba(225,29,72,0.3)',
            borderTopColor: '#e11d48',
            animation: 'spin 0.7s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (user && !(user as any).emailVerified && !(user as any).isSeed && !(user as any).hasSocialLogin) {
    const allowedPaths = ['/verify-email', '/onboarding'];
    if (!allowedPaths.some(p => location.startsWith(p))) {
      return <Redirect to="/verify-email" />;
    }
  }

  if (requireOnboarding && user && !user.onboardingComplete) {
    return <Redirect to="/onboarding" />;
  }

  if (!requireOnboarding && user && user.onboardingComplete) {
    return <Redirect to="/discover" />;
  }

  return <Component />;
}

function BackButtonGuard() {
  const { token } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    window.history.pushState({ page: 'app' }, '', window.location.pathname);

    const MAIN_ROUTES = ['/discover', '/explore', '/likes', '/matches', '/profile'];

    const handlePopState = () => {
      const currentPath = window.location.pathname;

      if (currentPath.startsWith('/api/') || currentPath.includes('/callback')) {
        window.history.pushState({ page: 'app' }, '', '/discover');
        navigate('/discover');
        return;
      }

      if (MAIN_ROUTES.some(r => currentPath === r || currentPath.startsWith(r + '/'))) {
        window.history.pushState({ page: 'app' }, '', currentPath);
        if (currentPath !== '/discover') {
          navigate('/discover');
        }
        return;
      }

      if (currentPath.startsWith('/chat/') || currentPath.startsWith('/explore/')) {
        return;
      }

      window.history.pushState({ page: 'app' }, '', currentPath);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [token, navigate]);
  return null;
}

function PageViewTracker() {
  const [location] = useLocation();
  useEffect(() => {
    pixelPageView();
  }, [location]);
  return null;
}

function BannerAdWrapper() {
  const [location] = useLocation();
  const hiddenPaths = [
    '/login',
    '/register',
    '/admin',
    '/operator',
    '/onboarding',
    '/privacy',
    '/terms',
    '/delete-account',
  ];
  if (location.startsWith('/explore/')) return null;
  const shouldHide = hiddenPaths.some(p => location === p || location.startsWith(p + '/'));
  if (shouldHide) return null;
  return (
    <>
      <BannerAd placement="standard" />
      <BannerAd placement="global" />
    </>
  );
}

type NotifItem = {
  id: number;
  title: string;
  message: string;
  photo?: string | null;
  action?: () => void;
};

function HeartbeatLayer() {
  useHeartbeat();
  return null;
}

function MatchNotificationLayer() {
  const [, navigate] = useLocation();
  const [notifications, setNotifications] = useState<NotifItem[]>([]);
  const { refresh: refreshSubscription } = useSubscription();

  useWebSocket();

  useEffect(() => {
    const handleSwMessage = (event: MessageEvent) => {
      if (!navigator.serviceWorker?.controller || event.source !== navigator.serviceWorker.controller) {
        return;
      }
      if (event.data?.type === 'NAVIGATE' && typeof event.data.url === 'string') {
        let url: string = event.data.url;
        if (url.startsWith('http://') || url.startsWith('https://')) {
          try {
            const parsed = new URL(url);
            if (parsed.hostname === 'papacitoapp.com' || parsed.hostname.endsWith('.papacitoapp.com')) {
              url = parsed.pathname + parsed.search + parsed.hash || '/';
            } else {
              console.warn('[App] Blocked external NAVIGATE URL:', url);
              return;
            }
          } catch {
            console.warn('[App] Blocked malformed NAVIGATE URL:', url);
            return;
          }
        }
        if (!url.startsWith('/') || url.startsWith('//')) {
          console.warn('[App] Blocked non-relative NAVIGATE URL:', url);
          return;
        }
        navigate(url);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
  }, [navigate]);

  useEffect(() => {
    return onGlobalSubscriptionRefresh(() => {
      refreshSubscription();
    });
  }, [refreshSubscription]);

  useEffect(() => {
    return onGlobalMatch((data: MatchNotification) => {
      playMatchSound();

      const notif: NotifItem = {
        id: Date.now(),
        title: `You matched with ${data.matchedWith?.name || 'someone'}! 🔥`,
        message: 'Tap to say hello',
        photo: data.matchedWith?.photo,
        action: () => navigate(`/chat/${data.matchId}`),
      };

      setNotifications(prev => [...prev, notif]);
    });
  }, [navigate]);

  const dismiss = useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  return (
    <>
      <AnimatePresence>
        {notifications.map(notif => (
          <NotificationBanner
            key={notif.id}
            title={notif.title}
            message={notif.message}
            photo={notif.photo}
            action={notif.action}
            onDismiss={() => dismiss(notif.id)}
          />
        ))}
      </AnimatePresence>
      <MatchInstallNudge bannerActive={notifications.length > 0} />
    </>
  );
}

function PushPromptLayer() {
  useActiveTimeAccumulator();
  const { visible, copy, trigger, loading, accept, decline, notifyServerShown } = usePushPromptController();

  useEffect(() => {
    if (visible && copy && trigger) {
      notifyServerShown(trigger);
    }
  }, [visible, trigger, copy, notifyServerShown]);

  return (
    <AnimatePresence>
      {visible && copy && (
        <SoftPushPrompt
          key="soft-push-prompt"
          title={copy.title}
          body={copy.body}
          cta={copy.cta}
          loading={loading}
          onAccept={accept}
          onDecline={decline}
        />
      )}
    </AnimatePresence>
  );
}

// FIX-D: Routes where the behavioral push opt-in modal must never appear.
// Mirrors the SUPPRESSED_PATHS list used by PushDeclinedBanner /
// MatchInstallNudge / IOSInstallBanner exactly: signup, email verification,
// onboarding, the VIP offer and other auth/full-screen flows must not be
// interrupted by a behavior/timer-driven push nag. Root "/" and
// marketing/landing routes are intentionally NOT listed — behavioral push
// must still work in-app (/discover, /likes, /chat, /explore, etc.).
const BEHAVIORAL_PUSH_SUPPRESSED_PATHS = [
  '/login',
  '/register',
  '/verify-email',
  '/onboarding',
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/install-prompt',
  '/push-prompt',
  '/vip-offer',
  '/open-in-chrome',
  '/install-app',
  '/survey',
  '/subscription',
  '/delete-account',
  '/legal',
  '/privacy',
  '/terms',
];

function BehavioralPushLayer() {
  const [location] = useLocation();
  const isSuppressedPath = BEHAVIORAL_PUSH_SUPPRESSED_PATHS.some(
    p => location === p || location.startsWith(p + '/'),
  );
  const {
    visible,
    variant,
    loading,
    blurredPhotoUrl,
    iosGateVisible,
    contextMetadata,
    triggerVariantA,
    triggerVariantB,
    triggerVariantC,
    triggerVariantD,
    triggerVariantE,
    triggerVariantF,
    accept,
    decline,
    dismissIosGate,
    showIosGuide,
  } = useBehavioralPushOptIn();

  // Listen for cross-component triggers (Discover swipes, MatchCelebration,
  // Matches first-message arrival).
  useEffect(() => {
    // FIX-D: do not arm/dispatch any behavioral variant on suppressed funnel
    // routes (register → verify-email → onboarding → vip-offer, etc.).
    if (isSuppressedPath) return;
    return onBehavioralPushTrigger((e) => {
      if (e.type === 'A') triggerVariantA();
      else if (e.type === 'B') triggerVariantB(e.matchId);
      else if (e.type === 'C') triggerVariantC();
      else if (e.type === 'D') triggerVariantD();
      else if (e.type === 'E') triggerVariantE();
      else if (e.type === 'F') triggerVariantF(e.direction ?? 'like');
    });
  }, [isSuppressedPath, triggerVariantA, triggerVariantB, triggerVariantC, triggerVariantD, triggerVariantE, triggerVariantF]);

  // Variant D "Welcome": fallback poll. Runs 15s after mount to give the hook
  // time to hydrate context + respect the session-start gate, then re-checks
  // every 15s while still eligible. The hook itself enforces cooldown,
  // onboarding/photo gates, and max-prompts-per-session — we only schedule
  // the attempt.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // FIX-D: do not arm the Variant D "Welcome" fallback poll on suppressed
    // funnel routes. This poll was the path that popped "Stay in the loop 🔔"
    // (D_welcome) on top of /vip-offer for onboarding-complete returning users.
    if (isSuppressedPath) return;
    const tryD = () => { triggerVariantD(); };
    const initial = window.setTimeout(tryD, 16 * 1000);
    const interval = window.setInterval(tryD, 15 * 1000);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
    };
  }, [isSuppressedPath, triggerVariantD]);

  // Variant C: exit intent via visibilitychange + 30s AFK timer.
  // Requires session ≥ 3 minutes before arming.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // FIX-D: do not arm the Variant C exit-intent listeners on suppressed
    // funnel routes.
    if (isSuppressedPath) return;
    let leftAt: number | null = null;
    let pendingTrigger = false;

    const sessionStartMs = (() => {
      try {
        const v = sessionStorage.getItem('papacito_session_start_at');
        return v ? Number(v) : Date.now();
      } catch { return Date.now(); }
    })();

    const onVisibility = () => {
      if (Date.now() - sessionStartMs < 3 * 60 * 1000) return;
      if (document.visibilityState === 'hidden') {
        leftAt = Date.now();
      } else if (document.visibilityState === 'visible' && leftAt) {
        const away = Date.now() - leftAt;
        leftAt = null;
        if (away >= 30 * 1000) {
          pendingTrigger = true;
        }
      }
    };

    const tryFire = () => {
      if (!pendingTrigger) return;
      pendingTrigger = false;
      triggerVariantC();
    };

    document.addEventListener('visibilitychange', onVisibility);
    // Fire on next render after a return — checked on focus.
    window.addEventListener('focus', tryFire);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', tryFire);
    };
  }, [isSuppressedPath, triggerVariantC]);

  // FIX-D defense-in-depth: even if a trigger slipped through (e.g. a variant
  // armed just before navigating onto a suppressed route), never RENDER the
  // behavioral modal / iOS gate on funnel pages.
  if (isSuppressedPath) return null;

  if (iosGateVisible) {
    return (
      <AnimatePresence>
        <IOSInstallGateModal onShowGuide={showIosGuide} onDismiss={dismissIosGate} />
      </AnimatePresence>
    );
  }

  if (!visible || !variant) return null;
  const baseCopy = VARIANT_COPY[variant];
  const subtitle = buildVariantSubtitle(variant, contextMetadata);
  const title = buildVariantTitle(variant, contextMetadata);

  return (
    <AnimatePresence>
      <BehavioralPushModal
        key={variant}
        variant={variant}
        title={title}
        subtitle={subtitle}
        body={baseCopy.body}
        primaryCta={baseCopy.cta}
        secondaryCta={variant === 'E_first_message' ? 'Not right now' : undefined}
        loading={loading}
        blurredPhotoUrl={blurredPhotoUrl}
        onAccept={accept}
        onDecline={decline}
      />
    </AnimatePresence>
  );
}

function InstallNudgeLayer() {
  const { user, token } = useAuth();
  const isLoggedIn = !!token && !!user;
  const [showPersistentBanner, setShowPersistentBanner] = useState(false);
  const [showReturnNudge, setShowReturnNudge] = useState(false);

  useEffect(() => {
    if (shouldShowInstallNudge() && !isTriggerDismissed('persistent_banner')) {
      const timer = setTimeout(() => setShowPersistentBanner(true), 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const sessionCount = trackSession();
    if (
      sessionCount >= 2 &&
      sessionCount <= 5 &&
      !isStandalone() &&
      !isTriggerDismissed('return_visit')
    ) {
      const timer = setTimeout(() => setShowReturnNudge(true), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLoggedIn]);

  useEffect(() => {
    if (!user || !token) return;

    const reRegisterPush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        if (Notification.permission !== 'granted') return;

        const registration = await navigator.serviceWorker.ready;
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) return;

        const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
        if (!vapidKey) return;

        const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
        const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const keyArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) keyArray[i] = rawData.charCodeAt(i);

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyArray as BufferSource,
        });

        const subJson = subscription.toJSON();
        if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) return;

        await fetch(apiUrl('/api/users/push-subscription'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription: subJson }),
        });

        console.log('[Push] Re-registered subscription silently');
      } catch (err) {
        console.error('[Push] Re-registration failed:', err);
      }
    };

    reRegisterPush();
  }, [user?.id, token]);

  useEffect(() => {
    if (!token) return;
    const detectPlatformLabel = (): string => {
      const ua = navigator.userAgent;
      if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
      if (/Android/i.test(ua)) return 'android';
      return 'desktop';
    };
    const handler = async () => {
      try {
        await fetch(apiUrl('/api/users/pwa-installed'), {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isPwaInstalled: true,
            installTrigger: localStorage.getItem('papacito_last_install_trigger') || 'unknown',
            platform: detectPlatformLabel(),
          }),
        });
      } catch {}
    };
    window.addEventListener('appinstalled', handler);

    if (isStandalone()) {
      fetch(apiUrl('/api/users/pwa-installed'), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isPwaInstalled: true,
          installTrigger: localStorage.getItem('papacito_last_install_trigger') || 'standalone_detect',
          platform: detectPlatformLabel(),
        }),
      }).catch(() => {});
    }

    return () => window.removeEventListener('appinstalled', handler);
  }, [token]);

  return (
    <>
      {showPersistentBanner && isLoggedIn && !isStandalone() && (
        <InstallNudge
          triggerId="persistent_banner"
          emoji="📲"
          title="Install for instant match alerts"
          subtitle=""
          ctaText="Install"
          onDismiss={() => setShowPersistentBanner(false)}
          variant="banner"
        />
      )}
      {showReturnNudge && (
        <InstallNudge
          triggerId="return_visit"
          emoji="👋"
          title="Welcome back!"
          subtitle="Save Papacito to your home screen for 1-tap access and instant match notifications. Takes 10 seconds."
          ctaText="📲 Save to Home Screen"
          onDismiss={() => setShowReturnNudge(false)}
          variant="bottom_sheet"
        />
      )}
    </>
  );
}

function InstallPromptGuard() {
  const [location] = useLocation();
  const INSTALL_ALLOWED_PATHS = [
    '/discover',
    '/explore',
    '/likes',
    '/chat',
    '/matches',
    '/profile',
  ];
  const shouldShowInstall = INSTALL_ALLOWED_PATHS.some(p => location.startsWith(p));
  if (!shouldShowInstall) return null;
  const isOnDiscover = location.startsWith('/discover');
  return (
    <>
      {!isInstalled() && isAndroid() && <AndroidInstallPrompt />}
      {!isAndroid() && <InstallPrompt />}
      {!isOnDiscover && <InstallNudgeLayer />}
    </>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route
          path="/"
          component={() => {
            const { token, isLoading } = useAuth();
            if (isLoading) return <PageLoader />;
            if (token) return <Redirect to="/discover" />;
            return <Landing />;
          }}
        />
        <Route
          path="/login"
          component={() => (
            <ErrorBoundary>
              <Login />
            </ErrorBoundary>
          )}
        />
        <Route path="/register" component={Register} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/subscription-success" component={SubscriptionSuccess} />
        <Route path="/unsubscribe" component={Unsubscribe} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/delete-account" component={DeleteAccount} />
        <Route
          path="/onboarding"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Onboarding} requireOnboarding={false} />
            </ErrorBoundary>
          )}
        />
        {/* 2026-05-23: legacy /survey shim now redirects to /push-prompt,
            mirroring the Onboarding final-step revert. Keep the redirect
            so any in-flight users with /survey in browser history land in
            the right place. */}
        <Route path="/survey">{() => <Redirect to="/push-prompt" />}</Route>
        <Route
          path="/vip-offer"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={VipSalesScreen} />
            </ErrorBoundary>
          )}
        />
        {/* 2026-05-12 — repurposed post-signup acceptance page. */}
        <Route
          path="/push-prompt"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={PushPromptPage} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/open-in-chrome"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={OpenInChromeScreen} />
            </ErrorBoundary>
          )}
        />
        {/* Backwards-compat: legacy /install-prompt path → /push-prompt.
            Some in-flight survey flows may still hit it before the deploy
            propagates. */}
        <Route path="/install-prompt">{() => <Redirect to="/push-prompt" />}</Route>
        {/* Destination of the install email CTA link. NOT /install — that
            path is owned by the Express ad-lander. See pages/Install.tsx
            header comment for the full reasoning. */}
        <Route
          path="/install-app"
          component={() => (
            <ErrorBoundary>
              <InstallPage />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/discover"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Discover} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/explore"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Explore} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/explore/:categoryId"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={ExploreCategory} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/likes"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Likes} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/matches"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Matches} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/chat/:matchId"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Chat} />
            </ErrorBoundary>
          )}
        />
        <Route
          path="/profile"
          component={() => (
            <ErrorBoundary>
              <ProtectedRoute component={Profile} />
            </ErrorBoundary>
          )}
        />
        <Route path="/profile/:userId" component={PublicProfile} />
        <Route path="/admin" component={Admin} />
        <Route
          path="/operator/login"
          component={() => (
            <ErrorBoundary>
              <OperatorLogin />
            </ErrorBoundary>
          )}
        />
        <Route path="/operator/setup" component={OperatorSetup} />
        <Route path="/operator/dashboard" component={OperatorDashboard} />
        <Route
          path="/operator"
          component={() => (
            <ErrorBoundary>
              <OperatorLogin />
            </ErrorBoundary>
          )}
        />
        <Route path="/swipe">{() => <Redirect to="/discover" />}</Route>
        <Route path="/chat">{() => <Redirect to="/matches" />}</Route>
        <Route path="/home">{() => <Redirect to="/discover" />}</Route>
        <Route component={NotFound} />
      </Switch>
      <BackButtonGuard />
      <PageViewTracker />
      <BannerAdWrapper />
      <InstallPromptGuard />
    </Suspense>
  );
}

function SubscriptionBridge({ children }: { children: React.ReactNode }) {
  const { token, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const handleVipConfirmed = useCallback(() => {
    refreshUser();
    queryClient.invalidateQueries({ queryKey: ['messages'] });
    queryClient.invalidateQueries({ queryKey: ['getMessages'] });
    // FB Purchase event is now fired server-side from the Whop webhook (see
    // webhooks-whop.ts) for reliability. The previous client-side fbq()
    // here was unreliable (iOS ITP, ad blockers, users not returning to the
    // SPA after Whop checkout) and was double-counting now that CAPI fires.
  }, [refreshUser, queryClient]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as { seedName?: string | null } | undefined;
      const seedName = detail?.seedName || 'She';
      toast({
        title: '🔥 You matched!',
        description: `${seedName} wants to meet up. Open the chat now.`,
      });
      navigate('/likes');
    };
    window.addEventListener('papacito:vip_meetup_unlocked', handler);
    return () => window.removeEventListener('papacito:vip_meetup_unlocked', handler);
  }, [navigate]);

  return (
    <SubscriptionProvider token={token} onVipConfirmed={handleVipConfirmed}>
      {children}
    </SubscriptionProvider>
  );
}

function App() {
  useEffect(() => {
    localStorage.removeItem('papacito_domain_banner');
  }, []);

  useEffect(() => {
    const clearBadge = () => {
      if (document.visibilityState === 'visible' && 'clearAppBadge' in navigator) {
        (navigator as any).clearAppBadge().catch(() => {});
      }
    };
    clearBadge();
    document.addEventListener('visibilitychange', clearBadge);
    window.addEventListener('focus', clearBadge);
    return () => {
      document.removeEventListener('visibilitychange', clearBadge);
      window.removeEventListener('focus', clearBadge);
    };
  }, []);

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<div>Something went wrong</div>}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
            <AuthProvider>
              <SubscriptionBridge>
                <HeartbeatLayer />
                <MatchNotificationLayer />
                {/* [#342] PushPromptLayer disabled — replaced by BehavioralPushLayer (variants A/B/C) + iOS install gate. */}
                <BehavioralPushLayer />
                <PushDeclinedBanner />
                {/* In-app WebView (FB/IG/TikTok) detector — full-screen overlay
                    telling the user to open the page in Safari/Chrome.
                    Renders null when not inside an in-app browser. */}
                <InAppBrowserOverlay />
                <IOSInstallBanner />
                {/* Desktop-only left rail; returns null on tablet/mobile. */}
                <DesktopSidebar />
                <Router />
              </SubscriptionBridge>
            </AuthProvider>
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
